import { RefObject } from "react";
import useImpressionOnce from "hooks/useImpressionOnce";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { setSearchRecommendationAttribution } from "@Utils/searchRecommendationAttribution";
import { RecommendationCardContext } from "./types";

/**
 * Remember where the user was before leaving for a property page. The search page restores
 * it on remount when it does not need to refetch (fetchSearchDataIfNeed) — FR-32.
 */
export const saveScrollPositionForBackNav = () => {
  try {
    window.history.replaceState(
      { ...(window.history.state || {}), scrollY: window.scrollY },
      "",
      window.location.href,
    );
  } catch {
    // history unavailable (sandboxed iframe) — restoration is best-effort
  }
};

/** Fires the card impression once `ref` has been seen. No-op for a regular card. */
export const useRecommendationCard = (
  ref: RefObject<Element | null>,
  recommendation?: RecommendationCardContext,
) =>
  useImpressionOnce(
    ref,
    () => gtagEventEmitter({ action: "search_recommendation_card_viewed", ...recommendation }),
    { enabled: Boolean(recommendation) },
  );

/** What a recommendation link needs to attribute the property page view (contract §7.4). */
type RecommendationClickContext = Pick<
  RecommendationCardContext,
  "step_index" | "removed_filters" | "removed_filter_groups"
> & {
  month_widened?: boolean;
};

/**
 * Remember that a recommended card (or its "View rooms" CTA / map pin) opened `canonical_name`,
 * with the filter params its link passed. `partner_id` is not a filter and is left out.
 */
export const rememberRecommendationClick = (
  recommendation: RecommendationClickContext,
  canonical_name = "",
  passedFilters: Record<string, any> = {},
) => {
  setSearchRecommendationAttribution({
    canonical_name,
    step_index: recommendation.step_index,
    removed_filters: recommendation.removed_filters || [],
    removed_filter_groups: recommendation.removed_filter_groups || [],
    month_widened: Boolean(recommendation.month_widened),
    passed_filters: Object.fromEntries(
      Object.entries(passedFilters || {})
        .filter(([k]) => k !== "partner_id")
        .map(([k, v]) => [k, String(v)]),
    ),
  });
};

type ClickInput = {
  recommendation?: RecommendationCardContext;
  /** The card link's filter params (after `recommendedPropertyParams`). */
  passedFilters?: Record<string, any>;
  name: string;
  location: string;
  canonical_name?: string;
  category: string;
  email?: string;
};

/** Card click: scroll bookmark, then the recommendation event + attribution or the plain event. */
export const emitCardClick = ({
  recommendation,
  name,
  location,
  canonical_name = "",
  category,
  email,
  passedFilters,
}: ClickInput) => {
  saveScrollPositionForBackNav();
  if (recommendation) {
    gtagEventEmitter({
      action: "search_recommendation_card_clicked",
      label: `${name}${location}`,
      canonical_name,
      ...recommendation,
    });
    rememberRecommendationClick(recommendation, canonical_name, passedFilters);
    return;
  }
  gtagEventEmitter({
    action: "inventory_card_clicked",
    category,
    label: `${name}${location}`,
    canonical_name,
    email_id: email,
  });
};
